// pages/Community/SkoolProspects.jsx
//
// Where every Skool person stands, as a board — and the operator's override.
//
// The state is decided by the worker (services/prospectClassifier) and it is
// checked BEFORE every other lane rule: a booked person hears only the four
// booking reminders, a not-interested person hears nothing we start, an engaged
// or close-to-converting one gets no participation warnings. So this board is
// also a control panel: drag a card to another column (or pick a state in the
// drawer) and the person is pinned there — "manual" beats booking, rule and AI
// until you press "Back to automatic".
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import addNotification from "helpers/notify";
import { getSkoolProspects, setSkoolProspectState } from "api/connectors/connectors";

export const COLUMNS = [
  { key: "new", label: "New", color: "#667085", bg: "#f2f3f5", hint: "No real interaction yet. Every lane behaves normally." },
  { key: "engaged", label: "Engaged", color: "#0b3d91", bg: "#eef4ff", hint: "Talking to us or taking part. No onboarding warnings or nudges." },
  { key: "close_to_converting", label: "Close to converting", color: "#b54708", bg: "#fff4e5", hint: "Buying signals. No templated messages; gets context-based follow-ups." },
  { key: "booked", label: "Booked", color: "#6a3fb5", bg: "#f3eefe", hint: "A call is booked. Only the 4 reminder DMs + 4 emails, and replies to them." },
  { key: "converted", label: "Converted", color: "#1a7f37", bg: "#e7f7ec", hint: "Customer / in the build journey. Journey messages and replies only." },
  { key: "not_interested", label: "Not interested", color: "#b42318", bg: "#fdecea", hint: "Said no. Nothing we start; we still answer if they write." },
];
const BY_KEY = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));

const SOURCE_LABEL = { manual: "Set by you", booking: "Booking", rule: "Rule", ai: "AI" };
const fmt = (d) => { try { return d ? new Date(d).toLocaleString() : "—"; } catch (_) { return "—"; } };
const fmtDay = (d) => { try { return d ? new Date(d).toLocaleDateString() : "—"; } catch (_) { return "—"; } };
const profileUrl = (p) => (p.handle ? `https://www.skool.com/@${p.handle}` : "");
const displayName = (p) => p.name && !/^[a-z0-9-]+$/.test(p.name) ? p.name : (p.handle || p.name || p.userId);

function Pill({ children, color = "#667085", bg = "#f2f3f5", title }) {
  return (
    <span title={title} style={{ display: "inline-block", fontSize: "0.68rem", fontWeight: 600, color, background: bg, borderRadius: 10, padding: "1px 8px", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function Card({ p, onOpen, onDragStart }) {
  const b = p.booking || {};
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, p)}
      onClick={() => onOpen(p)}
      data-testid={`prospect-${p._id}`}
      style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 10px", marginBottom: 8, cursor: "grab", boxShadow: "0 1px 1px rgba(16,24,40,.04)" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
        <b style={{ fontSize: "0.84rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName(p)}</b>
        <Pill color={p.stateSource === "manual" ? "#0b3d91" : "#667085"} bg={p.stateSource === "manual" ? "#eef4ff" : "#f2f3f5"}>
          {SOURCE_LABEL[p.stateSource] || p.stateSource}
        </Pill>
      </div>
      {p.reason && (
        <div style={{ fontSize: "0.74rem", color: "#475467", marginTop: 4, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {p.reason}
        </div>
      )}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
        {b.startsAt && <Pill color="#6a3fb5" bg="#f3eefe">Call {fmt(b.startsAt)}</Pill>}
        {p.introduced && <Pill color="#1a7f37" bg="#e7f7ec">Introduced</Pill>}
        {p.followUps > 0 && <Pill>{p.followUps} follow-up{p.followUps > 1 ? "s" : ""}</Pill>}
        {p.email && <Pill title={p.email}>email</Pill>}
      </div>
    </div>
  );
}

function Drawer({ p, onClose, onSet, saving }) {
  const [state, setState] = useState(p.state);
  const [reason, setReason] = useState("");
  const b = p.booking || {};
  const col = BY_KEY[p.state] || COLUMNS[0];
  return (
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 420, maxWidth: "100%", background: "#fff", boxShadow: "-4px 0 16px rgba(16,24,40,.12)", zIndex: 1050, overflowY: "auto", padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h5 style={{ margin: 0 }}>{displayName(p)}</h5>
        <button className="btn btn-sm btn-light" onClick={onClose} aria-label="Close">✕</button>
      </div>
      {profileUrl(p) && <a href={profileUrl(p)} target="_blank" rel="noreferrer" style={{ fontSize: "0.8rem" }}>@{p.handle}</a>}
      {p.email && <div style={{ fontSize: "0.8rem", color: "#475467" }}>{p.email}</div>}

      <div style={{ marginTop: 14, padding: 10, background: col.bg, borderRadius: 8 }}>
        <div style={{ fontWeight: 700, color: col.color }}>{col.label}</div>
        <div style={{ fontSize: "0.76rem", color: "#475467" }}>{col.hint}</div>
        <div style={{ fontSize: "0.76rem", marginTop: 6 }}>
          <b>{SOURCE_LABEL[p.stateSource] || p.stateSource}</b>{p.classifiedAt ? ` · ${fmt(p.classifiedAt)}` : ""}
          {p.reason ? <div style={{ marginTop: 2 }}>{p.reason}</div> : null}
          {p.stateSource === "manual" && p.aiState && p.aiState !== p.state && (
            <div style={{ marginTop: 4, color: "#b54708" }}>The AI would say: <b>{(BY_KEY[p.aiState] || {}).label || p.aiState}</b></div>
          )}
        </div>
      </div>

      {b.startsAt && (
        <div style={{ marginTop: 14 }}>
          <div className="small font-weight-bold mb-1">Booking</div>
          <div style={{ fontSize: "0.8rem" }}>{b.service || "Call"} — {fmt(b.startsAt)}</div>
          {b.link && <a href={b.link} target="_blank" rel="noreferrer" style={{ fontSize: "0.78rem" }}>{b.link}</a>}
          <div style={{ fontSize: "0.72rem", color: "#667085" }}>Matched by {b.matchedBy || "?"}</div>
          {(p.reminders || []).length > 0 && (
            <ul style={{ fontSize: "0.76rem", paddingLeft: 18, marginTop: 6 }}>
              {p.reminders.map((r, i) => <li key={i}>{r.rung} · {r.channel} · {fmt(r.at)}</li>)}
            </ul>
          )}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <div className="small font-weight-bold mb-1">Set the state</div>
        <select className="form-control form-control-sm" value={state} onChange={(e) => setState(e.target.value)} aria-label="State">
          {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <input className="form-control form-control-sm mt-2" placeholder="Why (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="btn btn-sm btn-primary" disabled={saving} onClick={() => onSet(p, state, reason)}>Save</button>
          {p.stateSource === "manual" && (
            <button className="btn btn-sm btn-outline-secondary" disabled={saving} onClick={() => onSet(p, "auto", "")}>
              Back to automatic
            </button>
          )}
        </div>
        <div style={{ fontSize: "0.72rem", color: "#667085", marginTop: 6 }}>
          A state you set stays until you press "Back to automatic" — bookings and the AI will not move it.
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div className="small font-weight-bold mb-1">History</div>
        {(p.history || []).length === 0 && <div style={{ fontSize: "0.76rem", color: "#667085" }}>No changes yet.</div>}
        {[...(p.history || [])].reverse().map((h, i) => (
          <div key={i} style={{ fontSize: "0.76rem", borderLeft: "2px solid #e5e7eb", paddingLeft: 8, marginBottom: 6 }}>
            <b>{(BY_KEY[h.state] || {}).label || h.state}</b> · {SOURCE_LABEL[h.source] || h.source} · {fmtDay(h.at)}
            {h.reason ? <div style={{ color: "#475467" }}>{h.reason}</div> : null}
          </div>
        ))}
      </div>
      <div style={{ fontSize: "0.72rem", color: "#98a2b3", marginTop: 12 }}>Last message from them: {fmt(p.lastInboundAt)}</div>
    </div>
  );
}

export default function SkoolProspects({ brandId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [dragOver, setDragOver] = useState("");
  // The card being dragged. dataTransfer is not reliable everywhere (some
  // browsers hide getData during the drag), so the id also lives here.
  const dragging = useRef("");
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSkoolProspects(brandId ? { brandId } : {});
      if (mounted.current) setRows((res && res.prospects) || []);
    } catch (e) {
      addNotification(e?.message || e?.error || "Could not load Skool prospects", "error");
    } finally { if (mounted.current) setLoading(false); }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);
  // The classifier runs hourly and bookings every 5 min — refresh while open.
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((p) => [p.name, p.handle, p.email, p.reason].join(" ").toLowerCase().includes(s));
  }, [rows, q]);

  const setState = async (p, state, reason) => {
    if (state !== "auto" && state === p.state && p.stateSource === "manual") { setOpen(null); return; }
    setSaving(true);
    // Optimistic: the card moves now; a failure puts it back.
    const before = rows;
    if (state !== "auto") setRows((rs) => rs.map((r) => (r._id === p._id ? { ...r, state, stateSource: "manual", reason: reason || r.reason } : r)));
    try {
      await setSkoolProspectState(p._id, state, reason);
      addNotification(state === "auto" ? "Handed back to the classifier" : `Moved to ${(BY_KEY[state] || {}).label || state}`, "success");
      setOpen(null);
      load();
    } catch (e) {
      setRows(before);
      addNotification(e?.error || e?.message || "Could not change the state", "error");
    } finally { if (mounted.current) setSaving(false); }
  };

  const onDragStart = (e, p) => {
    dragging.current = p._id;
    try { if (e.dataTransfer) e.dataTransfer.setData("text/plain", p._id); } catch (_) { /* ref carries it */ }
  };
  const onDrop = (e, key) => {
    e.preventDefault();
    setDragOver("");
    let id = dragging.current;
    try { id = (e.dataTransfer && e.dataTransfer.getData("text/plain")) || id; } catch (_) { /* use the ref */ }
    dragging.current = "";
    const p = rows.find((r) => r._id === id);
    if (p && p.state !== key) setState(p, key, "moved on the board");
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input className="form-control form-control-sm" style={{ maxWidth: 260 }} placeholder="Search name, handle, email, reason…"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
        <span style={{ fontSize: "0.76rem", color: "#667085" }}>
          {rows.length} people · classified hourly · drag a card to override
        </span>
      </div>

      {/* A wrapping grid, not a sideways-scrolling row: the Connectors page is
          ~960px wide, where a row of six columns hid the last two off-screen. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, paddingBottom: 8 }}>
        {COLUMNS.map((c) => {
          const items = filtered.filter((p) => p.state === c.key);
          return (
            <div key={c.key}
              data-testid={`col-${c.key}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(c.key); }}
              onDragLeave={() => setDragOver("")}
              onDrop={(e) => onDrop(e, c.key)}
              style={{ background: dragOver === c.key ? c.bg : "#f9fafb", border: `1px solid ${dragOver === c.key ? c.color : "#eaecf0"}`, borderRadius: 10, padding: 8, minHeight: 160, maxHeight: 520, overflowY: "auto" }}>
              <div title={c.hint} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: "0.82rem", color: c.color }}>{c.label}</span>
                <Pill color={c.color} bg={c.bg}>{items.length}</Pill>
              </div>
              {items.map((p) => <Card key={p._id} p={p} onOpen={setOpen} onDragStart={onDragStart} />)}
              {!items.length && <div style={{ fontSize: "0.74rem", color: "#98a2b3", textAlign: "center", marginTop: 20 }}>—</div>}
            </div>
          );
        })}
      </div>

      {open && <Drawer p={open} onClose={() => setOpen(null)} onSet={setState} saving={saving} />}
    </div>
  );
}
